// Consumer must wrap app root in <SupervisionSignalRProvider> exported below. Registration happens in App.tsx.
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { getAccessToken } from '../services/authToken';
import { apiScopes } from '../config/authConfig';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type SupervisionEventChannel =
  | 'reviewEvent'
  | 'actionItemEvent'
  | 'meetingEvent';

export type ReviewEventType =
  | 'review.created'
  | 'review.updated'
  // A single checklist tick. Emitted fire-and-forget by UpdateReviewChecklist — the
  // second-busiest endpoint in the app and the one behind Helen's complaint, which until now
  // broadcast nothing at all, so two reviewers on one review never saw each other's ticks.
  // Carries a SMALL payload (review id + item id + state + actor); the client only needs to know
  // which review moved in order to invalidate, and the SignalR plan is Free_F1 (20k msgs/day).
  | 'review.checklistUpdated'
  | 'review.completed';

export type ActionItemEventType =
  | 'actionItem.created'
  | 'actionItem.updated'
  | 'actionItem.completed'
  | 'actionItem.batchUpdated';

export type MeetingEventType = 'meeting.created' | 'meeting.started' | 'meeting.ended';

export type SupervisionEventType =
  | ReviewEventType
  | ActionItemEventType
  | MeetingEventType;

export interface SupervisionEventEnvelope<TPayload = unknown> {
  eventType: SupervisionEventType;
  payload: TPayload;
  timestamp: string;
}

type EventHandler<TPayload = unknown> = (
  envelope: SupervisionEventEnvelope<TPayload>,
) => void;

/**
 * Realtime channel state, as far as the BROWSER can tell.
 *
 * `offline` is the load-bearing one and the reason this type exists at all: before it, a failed
 * connection produced a single `console.warn(... '(non-fatal)')` and nothing else, so the channel
 * was dead in production for ~4 months and the UI looked identical to a healthy one.
 */
export type RealtimeStatus = 'connecting' | 'connected' | 'reconnecting' | 'offline';

interface SupervisionSignalRContextValue {
  isConnected: boolean;
  /** Finer-grained than `isConnected` — distinguishes "trying" from "given up". */
  status: RealtimeStatus;
  subscribe: <TPayload = unknown>(
    eventType: SupervisionEventType,
    handler: EventHandler<TPayload>,
  ) => () => void;
}

// -----------------------------------------------------------------------------
// Context
// -----------------------------------------------------------------------------

const SupervisionSignalRContext =
  createContext<SupervisionSignalRContextValue | null>(null);

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

// SignalR hub base URL. The @microsoft/signalr client appends `/negotiate` itself, so this must
// be the API base (NOT `/api/negotiate` — that would post to `/api/negotiate/negotiate`). In
// production VITE_API_BASE_URL is the Function App origin, which the SPA calls directly; falling
// back to `/api` only works in local dev where a proxy fronts the Functions host.
const HUB_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * How many times the INITIAL connection is attempted before the channel is declared offline.
 *
 * ⚠ `withAutomaticReconnect()` does NOT cover this. It engages only after a connection has been
 * successfully established and subsequently lost — a failed FIRST `start()` is retried by nobody.
 * Before this loop existed, one failed start meant the channel stayed dead until the page was
 * reloaded, which is exactly the state the app shipped in for four months.
 */
export const MAX_START_ATTEMPTS = 3;

/** Backoff before retry N (index 0 is unused — the first attempt is immediate). */
const START_BACKOFF_MS = [0, 2_000, 6_000];

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Reports the realtime channel's own blindness to somewhere QUERYABLE.
 *
 * `console.warn` reaches nobody — that is the measured lesson, not an opinion: the previous
 * implementation warned on every failed start and the channel was still dead in production for
 * ~4 months. This posts to `POST realtime-diagnostics`, which logs a fixed `REALTIME_OFFLINE_BEACON`
 * token server-side.
 *
 * ⚠ Never throws and never rejects. A diagnostic that can break the page it is diagnosing is worse
 * than no diagnostic — every failure path here is swallowed on purpose.
 */
async function postOfflineBeacon(
  baseUrl: string,
  body: { reason: string; attempts: number; lastError: string; transport: string },
): Promise<void> {
  try {
    await fetch(`${baseUrl}/realtime-diagnostics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      // Survives a page teardown racing the beacon.
      keepalive: true,
    });
  } catch {
    // Deliberately silent. If the API is unreachable too, the beacon cannot help and must not
    // add a second error to the console.
  }
}

/** Error → short string, without letting a giant stack into a log line. */
function describeError(err: unknown): string {
  if (!err) return '';
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  return String(err);
}

function channelForEvent(eventType: SupervisionEventType): SupervisionEventChannel {
  if (eventType.startsWith('review.')) return 'reviewEvent';
  if (eventType.startsWith('actionItem.')) return 'actionItemEvent';
  if (eventType.startsWith('meeting.')) return 'meetingEvent';
  // Exhaustive default.
  throw new Error(`Unknown supervision event type: ${eventType}`);
}

function invalidateQueriesFor(
  queryClient: ReturnType<typeof useQueryClient>,
  eventType: SupervisionEventType,
) {
  if (eventType.startsWith('review.')) {
    // Keys must match the hooks: useReviews uses ['client-reviews'] / ['client-review'].
    queryClient.invalidateQueries({ queryKey: ['client-reviews'] });
    queryClient.invalidateQueries({ queryKey: ['client-review'] });
    queryClient.invalidateQueries({ queryKey: ['suggested-reviews'] });
    return;
  }
  if (eventType.startsWith('actionItem.')) {
    // useMeetings registers action items under ['action-items'].
    queryClient.invalidateQueries({ queryKey: ['action-items'] });
    return;
  }
  if (eventType.startsWith('meeting.')) {
    queryClient.invalidateQueries({ queryKey: ['meetings'] });
    return;
  }
}

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------

interface SupervisionSignalRProviderProps {
  children: ReactNode;
  /**
   * Override the SignalR hub base URL. The client appends `/negotiate`.
   * Defaults to VITE_API_BASE_URL (the Function App origin) or `/api`.
   */
  hubUrl?: string;
}

export function SupervisionSignalRProvider({
  children,
  hubUrl = HUB_BASE_URL,
}: SupervisionSignalRProviderProps) {
  const queryClient = useQueryClient();
  const { instance } = useMsal();
  const [status, setStatus] = useState<RealtimeStatus>('connecting');
  const isConnected = status === 'connected';

  // Per-event handler registry (Set keeps handler identity semantics).
  const handlersRef = useRef<
    Map<SupervisionEventType, Set<EventHandler>>
  >(new Map());

  const connectionRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    let cancelled = false;

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        // 🔴 Load-bearing, and it must stay explicit. @microsoft/signalr 8.x defaults
        // `withCredentials` to TRUE, which sends the negotiate POST in credentials mode
        // `include`. The browser then requires `Access-Control-Allow-Credentials: true` back,
        // and the Function App's CORS block is `supportCredentials: false` — so the response
        // carries no such header and the request is blocked before it leaves the tab. That
        // killed the realtime channel for the entire direct-call era, silently, because the
        // `.start()` failure below is logged as non-fatal.
        //
        // Nothing on this path uses cookies: negotiate is authenticated by the Bearer JWT that
        // `accessTokenFactory` attaches, which AuthMiddleware turns into `StaffUserId`. So the
        // honest fix is to stop asking for credentials — NOT to set `supportCredentials: true`
        // on the Function App, which would open a credentialed-CORS surface we have no use for.
        // Pinned by useSupervisionSignalR.test.tsx.
        withCredentials: false,
        // Attach the Azure AD staff token to the negotiate request. The global fetch
        // interceptor (main.tsx) does NOT reliably cover SignalR's own transport, so we
        // wire the token explicitly here — the backend negotiate endpoint is JWT-gated and
        // binds the connection to the caller's oid. The negotiate response's own accessToken
        // then authenticates the transport to the Azure SignalR service.
        accessTokenFactory: async () => {
          const token = await getAccessToken(instance, apiScopes);
          return token ?? '';
        },
      })
      .withAutomaticReconnect() // Built-in exponential backoff.
      .configureLogging(LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    const handleChannelMessage = (envelope: SupervisionEventEnvelope) => {
      if (!envelope || typeof envelope.eventType !== 'string') return;

      // Auto-invalidate React Query caches.
      invalidateQueriesFor(queryClient, envelope.eventType);

      // Fan out to subscribers for this specific event type.
      const handlers = handlersRef.current.get(envelope.eventType);
      if (handlers) {
        handlers.forEach((handler) => {
          try {
            handler(envelope);
          } catch (err) {
            console.error(
              '[SupervisionSignalR] handler threw for',
              envelope.eventType,
              err,
            );
          }
        });
      }
    };

    (['reviewEvent', 'actionItemEvent', 'meetingEvent'] as const).forEach(
      (channel) => {
        connection.on(channel, handleChannelMessage);
      },
    );

    connection.onreconnected(() => {
      if (!cancelled) setStatus('connected');
    });
    connection.onreconnecting(() => {
      if (!cancelled) setStatus('reconnecting');
    });
    // Fires once automatic reconnection has given up (or the server closed us). This is a
    // terminal state, not a transient one — say so in the UI rather than sitting on a spinner.
    connection.onclose((err) => {
      if (cancelled) return;
      setStatus('offline');
      void beacon('closed', describeError(err));
    });

    // Guarantees at most ONE beacon per provider lifetime. Without it, a flapping connection
    // would turn a diagnostic into a self-inflicted request flood against the app it is
    // diagnosing — the beacon must never be the reason the system is unwell.
    let beaconSent = false;
    const beacon = (reason: string, lastError: string) => {
      if (beaconSent) return Promise.resolve();
      beaconSent = true;
      return postOfflineBeacon(hubUrl, {
        reason,
        attempts: attemptsMade,
        lastError,
        transport: '',
      });
    };

    let attemptsMade = 0;

    const startWithRetry = async () => {
      let lastError: unknown = null;

      while (!cancelled && attemptsMade < MAX_START_ATTEMPTS) {
        try {
          await connection.start();
          if (cancelled) return;
          setStatus(
            connection.state === HubConnectionState.Connected ? 'connected' : 'offline',
          );
          return;
        } catch (err) {
          lastError = err;
          attemptsMade += 1;
          if (attemptsMade >= MAX_START_ATTEMPTS) break;
          await sleep(START_BACKOFF_MS[attemptsMade] ?? 6_000);
        }
      }

      if (cancelled) return;

      // Every attempt failed. The old code logged `(non-fatal)` here and stopped — which is how
      // the channel stayed dead in production for ~4 months without anyone noticing. Now the
      // failure is reported to a queryable place AND shown to the user.
      console.warn(
        `[SupervisionSignalR] realtime unavailable after ${attemptsMade} attempt(s)`,
        lastError,
      );
      setStatus('offline');
      void beacon('start-failed', describeError(lastError));
    };

    void startWithRetry();

    return () => {
      cancelled = true;
      connection.stop().catch(() => {
        /* ignore */
      });
      connectionRef.current = null;
    };
  }, [hubUrl, queryClient, instance]);

  const contextValue = useMemo<SupervisionSignalRContextValue>(() => {
    const subscribe: SupervisionSignalRContextValue['subscribe'] = (
      eventType,
      handler,
    ) => {
      let handlers = handlersRef.current.get(eventType);
      if (!handlers) {
        handlers = new Set();
        handlersRef.current.set(eventType, handlers);
      }
      handlers.add(handler as EventHandler);

      return () => {
        const set = handlersRef.current.get(eventType);
        if (!set) return;
        set.delete(handler as EventHandler);
        if (set.size === 0) {
          handlersRef.current.delete(eventType);
        }
      };
    };

    return { isConnected, status, subscribe };
  }, [isConnected, status]);

  return (
    <SupervisionSignalRContext.Provider value={contextValue}>
      {children}
    </SupervisionSignalRContext.Provider>
  );
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Subscribe to a SignalR event scoped to component lifecycle.
 *
 * React Query caches are invalidated automatically at the provider level on
 * every event — this hook is for additional side effects (toasts, highlight
 * state, optimistic UI, etc.).
 */
export function useSupervisionSignalR<TPayload = unknown>(
  eventType: SupervisionEventType,
  handler: EventHandler<TPayload>,
): { isConnected: boolean } {
  const ctx = useContext(SupervisionSignalRContext);
  if (!ctx) {
    throw new Error(
      'useSupervisionSignalR must be used inside <SupervisionSignalRProvider>',
    );
  }

  // Keep the latest handler without re-subscribing each render.
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const unsubscribe = ctx.subscribe<TPayload>(eventType, (envelope) => {
      handlerRef.current(envelope);
    });
    return unsubscribe;
  }, [ctx, eventType]);

  return { isConnected: ctx.isConnected };
}

/**
 * Lightweight accessor for just the connection state.
 */
export function useSupervisionSignalRConnectionState(): boolean {
  const ctx = useContext(SupervisionSignalRContext);
  return ctx?.isConnected ?? false;
}

/**
 * Realtime status for UI that needs to distinguish "still trying" from "given up".
 *
 * Defaults to `connecting` outside a provider rather than `offline`: absence of a provider is a
 * wiring mistake, and rendering a scary "Live updates offline" badge for it would cry wolf.
 */
export function useRealtimeStatus(): RealtimeStatus {
  const ctx = useContext(SupervisionSignalRContext);
  return ctx?.status ?? 'connecting';
}

// Export channel helper for advanced consumers (tests, etc.).
export { channelForEvent };
